import type { OfflineError, OfflineScene, OfflineSceneState } from "@novorender/api";
import type { Attributes } from "@opentelemetry/api";

import { telemetry } from "telemetry";

/** What the scene reads as after a failure: `"unreadable"` when `getState()` rejects too. */
type StateAfter = OfflineSceneState["kind"] | "unreadable";

/**
 * Reports an offline operation's failure with what a support ticket needs to say where it
 * stopped: the SDK's typed error -- its kind, phase, file and HTTP status -- and what the scene's
 * state reads as after it. Never rejects.
 * @param scene The scene the operation was of; `undefined` when there is none to read.
 */
export async function trackOfflineFailure({
    error,
    source,
    severity = "warning",
    scene,
}: {
    error: unknown;
    source: string;
    severity?: "warning" | "error";
    scene: Pick<OfflineScene, "getState"> | undefined;
}): Promise<void> {
    const stateAfter: StateAfter | undefined = await scene?.getState().then(
        (state) => state.kind,
        () => "unreadable" as const,
    );
    telemetry.trackCaughtException({
        error,
        source,
        category: "data",
        severity,
        extraAttributes: offlineFailureAttributes(error, stateAfter),
    });
}

function offlineFailureAttributes(error: unknown, stateAfter: StateAfter | undefined): Attributes {
    const offlineError = isOfflineError(error) ? error : undefined;
    return {
        "offline.error.kind": offlineError?.kind,
        "offline.error.phase": offlineError?.phase,
        "offline.error.path": offlineError?.path,
        "offline.error.httpStatus": offlineError?.httpStatus,
        "offline.stateAfter": stateAfter,
    };
}

function isOfflineError(error: unknown): error is OfflineError {
    return typeof error === "object" && error !== null && typeof (error as OfflineError).kind === "string";
}
